/**
 * Import BibTeX into a document's bibliography — the one request every
 * surface makes, so none of them drifts from the API again.
 *
 * Until 26 Sep every caller sent `{ bibtex }`; the API reads `bibTexContent`,
 * so every import from the panel was a 400. The API returns the entries it
 * wrote (a skipped duplicate is not returned), and the panel's summary is
 * worked out from that against what the document already had.
 */
export interface ImportSummary {
  imported: number;
  updated: number;
  skipped: number;
}

type Fetch = (url: string, init?: RequestInit) => Promise<Response>;

/** Entries in the text — @type{…}, not the @comment / @string / @preamble directives. */
export function countEntries(bibtex: string): number {
  return (bibtex.match(/@(?!comment\b|string\b|preamble\b)\w+\s*\{/gi) ?? []).length;
}

export function summarise(written: readonly string[], before: ReadonlySet<string>, inText: number): ImportSummary {
  const updated = written.filter((k) => before.has(k)).length;
  const imported = written.length - updated;
  return { imported, updated, skipped: Math.max(0, inText - written.length) };
}

export async function importBibtex(
  fetchWithAuth: Fetch,
  documentId: string,
  bibtex: string,
  overwrite: boolean,
  existingKeys: Iterable<string>,
): Promise<ImportSummary> {
  const before = new Set(existingKeys);
  const response = await fetchWithAuth(`/api/documents/${documentId}/bibliography/import`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ bibTexContent: bibtex, overwrite }),
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error((err as { message?: string; title?: string }).message ?? (err as { title?: string }).title ?? `Import failed (HTTP ${response.status})`);
  }
  const written = ((await response.json()) as Array<{ citeKey: string }>).map((e) => e.citeKey);
  return summarise(written, before, countEntries(bibtex));
}
